import { useState, useCallback } from 'react';
import { gamesApi, type BlackjackAction } from '../api/games';
import { useWalletStore } from '../store/walletStore';
import { ApiError } from '../api/functionsClient';
import type { BlackjackView } from '../types';

export function useBlackjack() {
  const [roundId, setRoundId] = useState<string | null>(null);
  const [view, setView] = useState<BlackjackView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setBalance = useWalletStore((s) => s.setBalance);

  const deal = useCallback(
    async (betAmount: number) => {
      setBusy(true);
      setError(null);
      try {
        const res = await gamesApi.blackjackDeal(betAmount);
        setRoundId(res.roundId);
        setView(res.view);
        setBalance(res.balanceAfter);
        return res;
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Deal failed');
        throw err;
      } finally {
        setBusy(false);
      }
    },
    [setBalance],
  );

  const act = useCallback(
    async (action: BlackjackAction) => {
      if (!roundId) return;
      setBusy(true);
      setError(null);
      try {
        const res = await gamesApi.blackjackAction(roundId, action);
        setView(res.roundState);
        setBalance(res.balanceAfter);
        if (res.roundState.phase === 'done') setRoundId(null);
        return res;
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Action failed');
        throw err;
      } finally {
        setBusy(false);
      }
    },
    [roundId, setBalance],
  );

  return {
    deal,
    hit: () => act('hit'),
    stand: () => act('stand'),
    double: () => act('double'),
    split: () => act('split'),
    insurance: () => act('insurance'),
    view,
    roundId,
    busy,
    error,
  };
}
